import { BIOMES, type BiomeId, type InventoryState } from '@shefira/shared';

import type { CssAtlas } from '../assets/dom-atlas.js';
import { AuctionBuyView } from './auction-buy-view.js';
import type { AuctionDesk, Tone, Wearer } from './auction-format.js';
import { AuctionMineView } from './auction-mine-view.js';
import { AuctionSellView } from './auction-sell-view.js';
import { benchHead, purseChip, setPurse } from './bench.js';
import { closeButton } from './hud.js';
import { hideItemHover } from './item-hover.js';
import { kitTitle } from './kit.js';
import { uiText } from './text.js';
import './smith.css';
import './auction.css';

/**
 * The auction house at a village building (ADR 0035), on the counters' kit
 * (bench.ts): three sides for its biome under the purse. « Acheter »
 * searches the lots over HTTP; « Vendre » lists, in the window itself, what
 * of the bag may be put up, and asks quantity and price; « Mes lots » holds
 * mine, to withdraw or collect. Nothing here touches the room: the server
 * pushes the bag back once a call went through.
 */

export type AuctionView = 'buy' | 'sell' | 'mine';

export interface AuctionPanelOptions {
  token: string;
  access: () => Promise<string>;
  icons: CssAtlas | null;
  inventory: () => InventoryState | undefined;
  /** For the cards' level and class warnings; without it nothing is flagged. */
  wearer?: () => Wearer;
}

const ANY_WEARER: Wearer = { level: Number.MAX_SAFE_INTEGER, classId: 'warrior' };

export class AuctionPanel {
  private readonly root: HTMLElement;
  private readonly tabs: Record<AuctionView, HTMLButtonElement>;
  private readonly mineBadge: HTMLElement;
  private readonly purse: HTMLElement;
  private readonly message: HTMLElement;
  private readonly buy: AuctionBuyView;
  private readonly sell: AuctionSellView;
  private readonly mine: AuctionMineView;
  private readonly village: HTMLElement;
  private readonly place: HTMLElement;
  private view: AuctionView = 'buy';
  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' || this.root.hidden || event.repeat) return;
    if (
      document.activeElement instanceof HTMLInputElement ||
      document.activeElement instanceof HTMLSelectElement
    ) {
      return;
    }
    // Escape steps back out of the sale form before it closes the window.
    if (this.view === 'sell' && this.sell.back()) return;
    this.close();
  };

  constructor(private readonly options: AuctionPanelOptions) {
    this.root = document.createElement('section');
    this.root.className = 'smith auction';
    this.root.hidden = true;
    this.root.setAttribute('aria-label', uiText('Hôtel des ventes'));

    const title = document.createElement('h2');
    title.className = 'smith__title auction__title';
    kitTitle(title, 'auction');
    const label = document.createElement('span');
    label.className = 'auction__heading';
    label.textContent = uiText('Hôtel des ventes');
    // The village rides beside the name; a narrow phone moves it by the purse.
    this.village = document.createElement('span');
    this.village.className = 'auction__village';
    this.place = document.createElement('span');
    this.place.className = 'auction__place';
    title.append(
      label,
      this.village,
      closeButton('Fermer (Échap)', () => {
        this.close();
      }),
    );

    const tabBar = document.createElement('div');
    tabBar.className = 'auction__tabs';
    tabBar.setAttribute('role', 'tablist');
    const tab = (view: AuctionView, text: string): HTMLButtonElement => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'auction__tab';
      button.dataset.view = view;
      button.setAttribute('role', 'tab');
      const name = document.createElement('span');
      name.className = 'auction__tab-label';
      name.textContent = uiText(text);
      button.append(name);
      button.addEventListener('click', () => {
        this.setView(view);
      });
      tabBar.append(button);
      return button;
    };
    this.tabs = {
      buy: tab('buy', 'Acheter'),
      sell: tab('sell', 'Vendre'),
      mine: tab('mine', 'Mes lots'),
    };
    this.mineBadge = document.createElement('span');
    this.mineBadge.className = 'auction__badge';
    this.mineBadge.hidden = true;
    this.tabs.mine.append(this.mineBadge);
    this.purse = purseChip();
    const head = benchHead(tabBar, this.purse);
    head.insertBefore(this.place, this.purse);
    head.classList.add('auction__head');

    this.message = document.createElement('p');
    this.message.className = 'smith__message auction__message';
    this.message.setAttribute('aria-live', 'polite');

    const desk: AuctionDesk = {
      token: options.token,
      access: options.access,
      icons: options.icons,
      wearer: () => this.options.wearer?.() ?? ANY_WEARER,
      inventory: options.inventory,
      say: (text, tone) => {
        this.say(text, tone);
      },
    };
    this.mine = new AuctionMineView(desk, {
      onChanged: () => {
        this.renderBadge();
        this.sell.render();
        this.buy.render();
      },
    });
    this.sell = new AuctionSellView(desk, {
      activeLots: () => this.mine.activeCount,
      onListed: () => {
        this.mine.refresh();
      },
    });
    this.buy = new AuctionBuyView(desk, {
      isMine: (id) => this.mine.owns(id),
      onBought: () => {
        this.mine.refresh();
      },
    });

    this.root.append(title, head, this.buy.root, this.sell.root, this.mine.root, this.message);
    document.body.append(this.root);
    window.addEventListener('keydown', this.onKeyDown);
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  /** Opens on the buying side and refreshes both the lots and mine. */
  open(biome: BiomeId): void {
    this.village.textContent = `· ${uiText(BIOMES[biome].village)}`;
    this.place.textContent = uiText(BIOMES[biome].village);
    this.root.hidden = false;
    this.say('', 'success');
    this.renderPurse();
    this.setView('buy');
    this.mine.refresh();
  }

  close(): void {
    if (this.root.hidden) return;
    this.root.hidden = true;
    this.sell.back();
    hideItemHover();
  }

  /** The room pushed the bag: the purse, what may be sold and what may be bought follow. */
  setInventory(_state: InventoryState): void {
    if (this.root.hidden) return;
    this.renderPurse();
    this.buy.render();
    this.sell.render();
  }

  destroy(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    this.buy.destroy();
    this.sell.destroy();
    this.root.remove();
  }

  private setView(view: AuctionView): void {
    this.view = view;
    this.root.dataset.view = view;
    for (const [key, button] of Object.entries(this.tabs)) {
      const active = key === view;
      button.classList.toggle('auction__tab--active', active);
      button.setAttribute('aria-selected', String(active));
    }
    this.buy.root.hidden = view !== 'buy';
    this.sell.root.hidden = view !== 'sell';
    this.mine.root.hidden = view !== 'mine';
    hideItemHover();
    this.sell.back();
    if (view === 'buy') this.buy.refresh();
    else if (view === 'sell') this.sell.render();
    else this.mine.refresh();
  }

  private renderPurse(): void {
    setPurse(this.purse, this.options.inventory()?.gold ?? 0);
  }

  /** How many lots are mine on the tab, gold when one waits to be collected. */
  private renderBadge(): void {
    const total = this.mine.lots.length;
    this.mineBadge.hidden = total === 0;
    this.mineBadge.textContent = String(total);
    this.mineBadge.classList.toggle(
      'auction__badge--collect',
      this.mine.lots.some((lot) => lot.status !== 'active'),
    );
  }

  private say(text: string, tone: Tone): void {
    this.message.textContent = text;
    this.message.dataset.tone = tone;
  }
}
